import React, { useMemo, useState } from 'react';
import {
  PhoneIcon,
  PhoneArrowUpRightIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChatBubbleLeftEllipsisIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import SmsConversationPopup from '../Sms/SmsConversationPopup';
import { LeadStatus, LeadSubStage } from '../../types/lead';
import StatusBadge from '../UI/StatusBadge';
import Button from '../UI/Button';
import AgentTagPicker from './AgentTagPicker';
import FollowUpDateEditor from './FollowUpDateEditor';
import LeadEditModal from './LeadEditModal';
import { downloadLatestLeadRecording } from '../../utils/recordingDownload';

export interface LeadResultCardData {
  id: string;
  fullName?: string | null;
  full_name?: string | null;
  phoneE164?: string | null;
  phone_e164?: string | null;
  email?: string | null;
  companyName?: string | null;
  company_name?: string | null;
  status: string;
  subStageId?: string | null;
  sub_stage_id?: string | null;
  subStage?: LeadSubStage | null;
  sub_stage?: LeadSubStage | null;
  sub_stage_name?: string | null;
  campaignId?: string | null;
  campaign_id?: string | null;
  campaign_name?: string | null;
  assignedAgent?: { id: string; name: string } | null;
  assigned_agent?: { id: string; name: string } | null;
  taggedAgents?: Array<{ id: string; name: string }>;
  tagged_agents?: Array<{ id: string; name: string }>;
  latest_call_status?: string | null;
  latestCallStatus?: string | null;
  latest_call_outcome?: string | null;
  latestCallOutcome?: string | null;
  latest_reason?: string | null;
  lastCallAt?: string | null;
  last_call_at?: string | null;
  followUpDate?: string | null;
  follow_up_date?: string | null;
  statusChangedAt?: string | null;
  status_changed_at?: string | null;
  notes?: string | null;
  dial_count?: number | null;
}

const PIPELINE_STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'app_out', label: 'App Out' },
  { value: 'app_in', label: 'App In' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'contract', label: 'Contract' },
  { value: 'funded', label: 'Funded' },
  { value: 'unqualified', label: 'Unqualified' },
];

/** Native-friendly select — avoids .input/.select flex styles that clip text */
const FIELD_SELECT =
  'block h-9 min-w-[10rem] rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50';

interface LeadResultCardProps {
  lead: LeadResultCardData;
  subStageOptions: LeadSubStage[];
  statusOptions?: { value: LeadStatus; label: string }[];
  updating?: boolean;
  showCampaign?: boolean;
  extraActions?: React.ReactNode;
  /** Call + inline edit on list card — main nav View Results only */
  enableContactActions?: boolean;
  onDial?: (e: React.MouseEvent) => void;
  dialing?: boolean;
  onLeadFieldUpdated?: () => void;
  /** Admin-only soft delete (move to trash). Button only renders when provided. */
  onDelete?: () => void;
  onViewDetails: () => void;
  onStatusChange: (status: LeadStatus, e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSubStageChange: (subStageId: string | null, e: React.ChangeEvent<HTMLSelectElement>) => void;
  onTagsUpdated: () => void;
  onFollowUpUpdated: () => void;
}

const formatCallDate = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const MetaItem: React.FC<{ icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }> = ({
  icon: Icon,
  children,
}) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
    <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" />
    <span className="truncate">{children}</span>
  </span>
);

const normalizeLabel = (value?: string | null) =>
  (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const LeadResultCard: React.FC<LeadResultCardProps> = ({
  lead,
  subStageOptions,
  updating = false,
  showCampaign = false,
  extraActions,
  enableContactActions = false,
  onDial,
  dialing = false,
  onLeadFieldUpdated,
  onDelete,
  onViewDetails,
  onStatusChange,
  onSubStageChange,
  onTagsUpdated,
  onFollowUpUpdated,
  statusOptions,
}) => {
  const [notesOpen, setNotesOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [downloadingRecording, setDownloadingRecording] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [displayCompany, setDisplayCompany] = useState<string | null>(null);
  const [displayEmail, setDisplayEmail] = useState<string | null>(null);

  const baseStatusOptions = statusOptions ?? PIPELINE_STATUS_OPTIONS;
  const statusSelectOptions = baseStatusOptions.some((o) => o.value === lead.status)
    ? baseStatusOptions
    : [
        ...baseStatusOptions,
        {
          value: lead.status as LeadStatus,
          label: lead.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        },
      ];

  const name = displayName ?? lead.fullName ?? lead.full_name ?? 'Unknown';
  const phone = lead.phoneE164 ?? lead.phone_e164;
  const email = displayEmail ?? lead.email;
  const company = displayCompany ?? lead.companyName ?? lead.company_name;

  const lastCall = lead.lastCallAt ?? lead.last_call_at;
  const statusChanged = lead.statusChangedAt ?? lead.status_changed_at;
  const callStatus = lead.latest_call_status ?? lead.latestCallStatus;
  const callOutcome = lead.latest_call_outcome ?? lead.latestCallOutcome;
  const followUp = lead.followUpDate ?? lead.follow_up_date;
  const notesText = lead.notes;
  const subStageId = lead.subStageId ?? lead.sub_stage_id;
  const subStageName = lead.subStage?.name ?? lead.sub_stage?.name ?? lead.sub_stage_name;
  const assigned = lead.assignedAgent ?? lead.assigned_agent;
  const tagged = lead.taggedAgents ?? lead.tagged_agents;

  // Always surface the sub-status when a lead has one (View Results requirement),
  // even when its label happens to match the main status label.
  const showSubStageBadge = !!subStageName;

  const effectiveSubStageOptions = useMemo(() => {
    if (!subStageId || subStageOptions.some((s) => s.id === subStageId)) {
      return subStageOptions;
    }
    if (subStageName) {
      return [
        { id: subStageId, name: subStageName, campaignId: '', mainStatus: lead.status as LeadStatus, sortOrder: 0, createdAt: '', updatedAt: '' },
        ...subStageOptions,
      ];
    }
    return subStageOptions;
  }, [subStageId, subStageName, subStageOptions, lead.status]);

  return (
    <article className="rounded-xl border border-border bg-card transition-colors hover:border-primary/30">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <h4 className="truncate text-base font-semibold text-foreground">{name}</h4>
          <StatusBadge status={lead.status} type="lead" size="sm" />
          {showSubStageBadge && (
            <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {subStageName}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {enableContactActions && (
            <Button
              variant="outlined"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setEditOpen(true);
              }}
              title="Edit lead"
              aria-label="Edit lead"
            >
              <PencilSquareIcon className="h-4 w-4" />
            </Button>
          )}
          {phone && (
            <Button
              variant="outlined"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSmsOpen(true);
              }}
              type="button"
              title="SMS conversation"
              aria-label="Open SMS conversation"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outlined"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
          >
            <EyeIcon className="mr-1.5 h-4 w-4" />
            View Details
          </Button>
          {onDelete && (
            <Button
              variant="outlined"
              size="sm"
              className="!border-destructive/40 !text-destructive hover:!bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="Move to trash"
              aria-label="Move lead to trash"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Contact row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/50 px-4 py-2.5">
        {phone && (
          enableContactActions ? (
            <span className="inline-flex items-center gap-1">
              <MetaItem icon={PhoneIcon}>
                <span className="text-foreground">{phone}</span>
              </MetaItem>
              {onDial && (
                <button
                  type="button"
                  onClick={onDial}
                  disabled={dialing}
                  title="Call lead"
                  aria-label="Call lead"
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PhoneArrowUpRightIcon className="h-4 w-4" />
                </button>
              )}
            </span>
          ) : (
            <MetaItem icon={PhoneIcon}>
              <a href={`tel:${phone}`} className="hover:text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                {phone}
              </a>
            </MetaItem>
          )
        )}
        {(enableContactActions || company) && (
          <MetaItem icon={BuildingOfficeIcon}>{company || '—'}</MetaItem>
        )}
        {(enableContactActions || email) && (
          <MetaItem icon={EnvelopeIcon}>
            {email ? (
              <a
                href={`mailto:${email}`}
                className="hover:text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {email}
              </a>
            ) : (
              <span>—</span>
            )}
          </MetaItem>
        )}
        {showCampaign && lead.campaign_name && (
          <span className="text-xs text-muted-foreground">{lead.campaign_name}</span>
        )}
        {assigned && (
          <span className="text-xs text-muted-foreground">Assigned: {assigned.name}</span>
        )}
      </div>

      {/* Call meta row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/50 px-4 py-2.5 text-sm">
        {lastCall ? (
          <MetaItem icon={CalendarDaysIcon}>Last call {formatCallDate(lastCall)}</MetaItem>
        ) : (
          <MetaItem icon={CalendarDaysIcon}>No calls yet</MetaItem>
        )}
        {statusChanged && (
          <span className="text-sm text-muted-foreground">
            Status changed: <span className="text-foreground">{new Date(statusChanged).toLocaleString()}</span>
          </span>
        )}
        {callOutcome ? (
          <span className="text-sm text-muted-foreground">
            Last outcome: <span className="text-foreground">{callOutcome}</span>
          </span>
        ) : callStatus ? (
          <span className="text-sm text-muted-foreground">
            Call status: <span className="capitalize text-foreground">{callStatus.replace(/_/g, ' ')}</span>
          </span>
        ) : null}
        {lead.dial_count != null && lead.dial_count > 0 && (
          <span className="text-sm text-muted-foreground">{lead.dial_count} calls</span>
        )}
        {lead.latest_reason && (
          <span className="text-sm text-destructive">{lead.latest_reason}</span>
        )}
        <FollowUpDateEditor
          leadId={lead.id}
          leadName={name}
          followUpDate={followUp}
          status={lead.status}
          onUpdated={onFollowUpUpdated}
          className="!inline-flex !w-auto !border-0 !px-0 !py-0 !text-sm"
        />
        {enableContactActions && lastCall && (
          <button
            type="button"
            disabled={downloadingRecording}
            onClick={(e) => {
              e.stopPropagation();
              setDownloadingRecording(true);
              void downloadLatestLeadRecording(lead.id, name).finally(() =>
                setDownloadingRecording(false)
              );
            }}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
            title="Download latest call recording"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            {downloadingRecording ? 'Downloading…' : 'Recording'}
          </button>
        )}
      </div>

      {notesText && (
        <div className="border-t border-border/50 px-4 py-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setNotesOpen((v) => !v);
            }}
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
            {notesOpen ? 'Hide notes' : 'Show notes'}
          </button>
          {notesOpen && (
            <p className="mt-2 rounded-lg bg-muted/40 p-3 text-sm text-foreground/90 whitespace-pre-wrap">{notesText}</p>
          )}
        </div>
      )}

      {/* Footer */}
      <div
        className="grid gap-4 border-t border-border/50 bg-muted/10 px-4 py-3 lg:grid-cols-[1fr_auto]"
        onClick={(e) => e.stopPropagation()}
      >
        <AgentTagPicker
          leadId={lead.id}
          taggedAgents={tagged}
          compact
          onUpdated={() => onTagsUpdated()}
        />

        <div className="flex flex-wrap items-end gap-3 lg:justify-end">
          {extraActions}
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Stage</span>
            <select
              className={FIELD_SELECT}
              value={lead.status}
              disabled={updating}
              onChange={(e) => onStatusChange(e.target.value as LeadStatus, e)}
            >
              {statusSelectOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          {effectiveSubStageOptions.length > 0 && (
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Sub-stage</span>
              <select
                className={FIELD_SELECT}
                value={subStageId || ''}
                disabled={updating}
                onChange={(e) => onSubStageChange(e.target.value || null, e)}
              >
                <option value="">None</option>
                {effectiveSubStageOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>
      {enableContactActions && (
        <LeadEditModal
          isOpen={editOpen}
          leadId={lead.id}
          initial={{
            fullName: lead.fullName ?? lead.full_name,
            email: lead.email,
            companyName: lead.companyName ?? lead.company_name,
            followUpDate: followUp,
            status: lead.status,
            notes: notesText,
          }}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            onLeadFieldUpdated?.();
          }}
        />
      )}

      {smsOpen && (
        <SmsConversationPopup
          open={smsOpen}
          onClose={() => setSmsOpen(false)}
          leadId={lead.id}
          leadName={name}
          companyName={company}
          leadPhone={phone}
        />
      )}
    </article>
  );
};

export default LeadResultCard;
